import { apiSlice } from './apiSlice';
export interface UserDetails {
  user_detail_id: string;
  user_id: string;
  integration_id?: string | null;
  user_agent?: string | null;
  timezone?: string | null;
  platform?: string | null;
  language?: string | null;
  ip_address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  user_intake_data?: Record<string, any> | null;
  status: number;
  created_at: string;
  created_id: string;
  updated_at?: string | null;
  updated_id?: string | null;
}

const normalizeGetAllUsersArg = (arg?: { roles?: string }) => ({
  roles: arg?.roles,
});

const normalizeGetUsersByCustomerIdArg = (arg: string | { customerId: string; roles?: string }) => {
  if (typeof arg === 'string') {
    return { customerId: arg, roles: undefined };
  }

  return {
    customerId: arg?.customerId || '',
    roles: arg?.roles,
  };
};

const normalizeGetUsersByClientIdArg = (arg: string | { clientId: string; roles?: string }) => {
  if (typeof arg === 'string') {
    return { clientId: arg, roles: undefined };
  }

  return {
    clientId: arg?.clientId || '',
    roles: arg?.roles,
  };
};

export const userApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAllUsers: builder.query({
      query: (arg) => {
        const { roles } = normalizeGetAllUsersArg(arg);

        return {
          url: `/api/users`,
          method: 'GET',
          params: roles ? { roles } : undefined,
        };
      },
      transformResponse: (response) => response.users,
      providesTags: ['Users'],
    }),
    getUserById: builder.query({
      query: (userId) => ({
        url: `/api/users/${userId}`,
        method: 'GET',
      }),
      transformResponse: (response) => response.user,
      providesTags: (result, error, userId) => [{ type: 'User', id: userId }],
    }),
    createUser: builder.mutation({
      query: (newUser) => ({
        url: `/api/users`,
        method: 'POST',
        body: newUser,
      }),
      invalidatesTags: ['Users', 'TokenLimit'],
    }),
    updateUser: builder.mutation({
      query: ({ userId, ...updatedUser }) => ({
        url: `/api/users/${userId}`,
        method: 'PUT',
        body: updatedUser,
      }),
      invalidatesTags: (result, error, { userId }) => [{ type: 'User', id: userId }, 'Users', 'TokenLimit'],
    }),
    updateUserStatus: builder.mutation({
      query: ({ token, status, customerId }) => ({
        url: `/api/users/status`,
        method: 'PUT',
        body: { token, status, customer_id: customerId },
      }),
      invalidatesTags: ['Users'],
    }),
    getUsersByCustomerId: builder.query({
      query: (arg) => {
        const { customerId, roles } = normalizeGetUsersByCustomerIdArg(arg);

        return {
          url: `/api/users/${customerId}/customer`,
          method: 'GET',
          params: roles ? { roles } : undefined,
        };
      },
      transformResponse: (response) => response.users,
      providesTags: ['Users'],
    }),
    getUsersByClientId: builder.query({
      query: (arg) => {
        const { clientId, roles } = normalizeGetUsersByClientIdArg(arg);

        return {
          url: `/api/users/${clientId}/client`,
          method: 'GET',
          params: roles ? { roles } : undefined,
        };
      },
      transformResponse: (response) => response.users,
      providesTags: ['Users'],
    }),
    resendInvitation: builder.mutation({
      query: ({ userId, email }) => ({
        url: `/api/users/${userId}/resend-invitation`,
        method: 'POST',
        body: { email },
      }),
      invalidatesTags: ['Users'],
    }),

    // ─── User Intake ───────────────────────────────────────────────
    getUserIntake: builder.query({
      query: (userId?) => ({
        url: `/api/user-intake`,
        method: 'GET',
        params: userId ? { user_id: userId } : undefined,
      }),
      transformResponse: (response) => response.user_intake_data,
      providesTags: ['UserIntake'],
    }),
    upsertUserIntake: builder.mutation({
      query: (userIntakeData) => ({
        url: `/api/user-intake`,
        method: 'POST',
        body: { user_intake_data: userIntakeData },
      }),
      invalidatesTags: ['UserIntake'],
    }),
    // ─── User Details ───────────────────────────────────────────────
    getUserDetail: builder.query({
      query: (params?: {
        user_id?: string;
        customer_id?: string;
        client_id?: string;
        status?: number;
        user_detail_id?: string;
        integration_id?: string;
      }) => ({
        url: `/api/user-details`,
        method: 'GET',
        params,
      }),
      transformResponse: (response) => response.user_details,
      providesTags: ['UserDetails'],
    }),
  }),
});

export const {
  useGetAllUsersQuery,
  useGetUserByIdQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useGetUsersByCustomerIdQuery,
  useGetUsersByClientIdQuery,
  useUpdateUserStatusMutation,
  useResendInvitationMutation,
  useGetUserIntakeQuery,
  useUpsertUserIntakeMutation,
  useGetUserDetailQuery,
} = userApiSlice;
